/**
 * career-cv-workbench — the Kompass CV workbench (route `/compass/cv`).
 *
 * The current draft panel (from `cv.Drafted`) with the central act "Version
 * finalisieren" (`cv.version`); the immutable version history, each version exportable
 * (MD / JSON-LD downloads) and printable (`/cv-print?version=N`); per-entry evidence
 * links (claim chips → the ledger); portrait upload (existing portrait upload path).
 * A DRAFT is visibly NOT exportable (law C6 made legible, not only enforced server-side).
 *
 * Fetch-projection surface (owner data, deliberate acts — no live push); mutations
 * re-fetch to reconcile from server truth.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, RouterToken, apiGet, apiPost, apiUpload } from "../services"
import type { CareerI18n } from "../../i18n"
import type { Router } from "@platform/web-kit"

interface CvEntry {
  section: "work" | "education"
  entryId: string
  title: string
  subtitle: string
  claimRefs: string[]
}
interface CvView {
  name: string
  label: string
  hasPortrait: boolean
  workCount: number
  educationCount: number
  skillCount: number
  entries: CvEntry[]
  isDraft: boolean
}
interface VersionView {
  version: number
  versionedAt: string
  name: string
}

export class CvWorkbenchVm implements ViewModel {
  readonly cv: Signal<CvView | null> = signal<CvView | null>(null)
  readonly versions: Signal<readonly VersionView[]> = signal<readonly VersionView[]>([])
  readonly fixing: Signal<boolean> = signal(false)
  readonly busyPortrait: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(private readonly router: Router, i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.cv, this.versions, this.fixing, this.busyPortrait, this.error, this.loaded]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    try {
      const [cv, versions] = await Promise.all([
        apiGet<{ cv: any; isDraft: boolean; links: Record<string, string[]> }>("/api/cv"),
        apiGet<{ versions: { version: number; versionedAt: string; cv: any }[] }>("/api/cv/versions"),
      ])
      this.cv.set(cv.cv ? toCvView(cv.cv, cv.isDraft, cv.links) : null)
      this.versions.set(versions.versions.map((v) => ({ version: v.version, versionedAt: v.versionedAt, name: String(v.cv?.basics?.name ?? "") })))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  async fixVersion(): Promise<void> {
    this.fixing.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/cv/version")
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.fixing.set(false)
    }
  }

  async pickPortrait(): Promise<void> {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) void this.uploadPortrait(file)
    }
    input.click()
  }

  private async uploadPortrait(file: File): Promise<void> {
    this.busyPortrait.set(true)
    this.error.set(null)
    try {
      const form = new FormData()
      form.set("file", file)
      form.set("portrait", "true")
      await apiUpload("/api/documents/upload", form)
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busyPortrait.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerCvWorkbench extends KitElement<CvWorkbenchVm> {
  protected createViewModel(ctx: KitContext): CvWorkbenchVm {
    return new CvWorkbenchVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  private entryList(vm: CvWorkbenchVm, cv: CvView): TemplateResult | typeof nothing {
    if (cv.entries.length === 0) return nothing
    return html`
      <section class="cv-entries surface-panel" aria-labelledby="cv-entries-title">
        <h2 id="cv-entries-title" class="surface-section-title" lang="de">${vm.t("cv.entries.title")}</h2>
        <ul class="cv-entry-list" role="list">
          ${repeat(cv.entries, (e) => `${e.section}:${e.entryId}`, (e) => html`
            <li class="cv-entry" urn=${`urn:career:cventry:${e.section}:${e.entryId}`}>
              <p class="cv-entry-title" lang="de">${e.title}${e.subtitle ? html` — <span class="cv-entry-sub">${e.subtitle}</span>` : nothing}</p>
              ${e.claimRefs.length > 0
                ? html`<a class="cv-entry-claims chip" href="/compass/evidence" data-act="nav" data-url="/compass/evidence" lang="de">${vm.t("cv.entries.claims", { n: e.claimRefs.length })}</a>`
                : html`<span class="cv-entry-noclaims chip is-gap" lang="de">${vm.t("cv.entries.noClaims")}</span>`}
            </li>
          `)}
        </ul>
      </section>
    `
  }

  private versionList(vm: CvWorkbenchVm): TemplateResult {
    const versions = vm.versions.value
    if (versions.length === 0) return html`<p class="surface-empty" lang="de">${vm.t("cv.versions.empty")}</p>`
    return html`
      <ul class="cv-version-list" role="list">
        ${repeat(versions, (v) => v.version, (v) => html`
          <li class="cv-version is-versioned" urn=${`urn:career:cv:v${v.version}`}>
            <span class="cv-version-label" lang="de">${vm.t("cv.versions.label", { n: v.version })}</span>
            <span class="cv-version-date chip" lang="de">${new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" }).format(new Date(v.versionedAt))}</span>
            <div class="cv-version-actions">
              <a class="cv-export" href=${`/api/cv/versions/${v.version}/export.md`} download lang="de">${vm.t("cv.export.md")}</a>
              <a class="cv-export" href=${`/api/cv/versions/${v.version}/export.jsonld`} download lang="de">${vm.t("cv.export.jsonld")}</a>
              <a class="cv-print-link" href=${`/cv-print?version=${v.version}`} target="_blank" rel="noopener" lang="de">${vm.t("cv.print")}</a>
            </div>
          </li>
        `)}
      </ul>
    `
  }

  protected render(vm: CvWorkbenchVm): TemplateResult {
    const cv = vm.cv.value
    const err = vm.error.value
    return html`
      <section class="surface cv-workbench" aria-labelledby="cv-title">
        <h1 id="cv-title" class="surface-title" lang="de">${vm.t("surface.lebenslauf.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("cv.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        ${!cv
          ? html`${vm.loaded.value ? html`<p class="surface-empty" lang="de">${vm.t("cv.empty")}</p>` : nothing}`
          : html`
              <section class=${classMap({ "cv-draft": true, "surface-panel": true, "is-draft": cv.isDraft })} aria-labelledby="cv-draft-title">
                <h2 id="cv-draft-title" class="surface-section-title" lang="de">${cv.isDraft ? vm.t("cv.draft.title") : vm.t("cv.current.title")}</h2>
                <dl class="surface-facts">
                  <dt lang="de">${vm.t("review.cv.name")}</dt><dd lang="de">${cv.name}</dd>
                  <dt lang="de">${vm.t("review.cv.stations")}</dt><dd lang="de">${vm.t("review.cv.stationsN", { n: cv.workCount })}</dd>
                  <dt lang="de">${vm.t("review.cv.skills")}</dt><dd lang="de">${vm.t("review.cv.skillsN", { n: cv.skillCount })}</dd>
                </dl>
                ${cv.isDraft ? html`<p class="cv-draft-note chip is-draft" lang="de">${vm.t("cv.draft.notExportable")}</p>` : nothing}
                <div class="cv-draft-actions">
                  <button data-act="fix-version" class=${classMap({ "is-pending": vm.fixing.value })} ?disabled=${vm.fixing.value} lang="de">
                    ${vm.fixing.value ? vm.t("cv.fixing") : vm.t("cv.fixVersion")}
                  </button>
                  <button data-act="upload-portrait" data-portrait="true" class=${classMap({ "is-pending": vm.busyPortrait.value })} ?disabled=${vm.busyPortrait.value} lang="de">
                    ${cv.hasPortrait ? vm.t("cv.portrait.replace") : vm.t("cv.portrait.add")}
                  </button>
                </div>
              </section>

              ${this.entryList(vm, cv)}
            `}

        <section class="cv-versions surface-panel" aria-labelledby="cv-versions-title">
          <h2 id="cv-versions-title" class="surface-section-title" lang="de">${vm.t("cv.versions.title")}</h2>
          ${this.versionList(vm)}
        </section>
      </section>
    `
  }

  protected override actions(vm: CvWorkbenchVm): ActionMap {
    return {
      "fix-version": () => void vm.fixVersion(),
      "upload-portrait": () => void vm.pickPortrait(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}

function toCvView(cv: any, isDraft: boolean, links: Record<string, string[]>): CvView {
  const entries: CvEntry[] = []
  const work = Array.isArray(cv.work) ? cv.work : []
  work.forEach((w: any, i: number) => {
    const inline = Array.isArray(w.evidenceClaims) ? w.evidenceClaims : []
    const linked = links[`work:${i}`] ?? []
    entries.push({ section: "work", entryId: String(i), title: String(w.name ?? ""), subtitle: String(w.position ?? ""), claimRefs: [...new Set([...inline, ...linked])] })
  })
  const education = Array.isArray(cv.education) ? cv.education : []
  education.forEach((e: any, i: number) => {
    const inline = Array.isArray(e.evidenceClaims) ? e.evidenceClaims : []
    const linked = links[`education:${i}`] ?? []
    entries.push({ section: "education", entryId: String(i), title: String(e.institution ?? ""), subtitle: [e.studyType, e.area].filter(Boolean).join(" · "), claimRefs: [...new Set([...inline, ...linked])] })
  })
  return {
    name: String(cv.basics?.name ?? ""),
    label: String(cv.basics?.label ?? ""),
    hasPortrait: Boolean(cv.basics?.image),
    workCount: work.length,
    educationCount: education.length,
    skillCount: Array.isArray(cv.skills) ? cv.skills.length : 0,
    entries,
    isDraft,
  }
}
